import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import ReactECharts from 'echarts-for-react';
import { DefaultButton, Dropdown, IDropdownOption } from '@fluentui/react';

// funciones y props conectadas
import * as ComponentFunctions from './SponsorProfitChart.Functions';
import { Sponsor } from '../../../models/Entities/Sponsor/Sponsor';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { computeProtocolFinancialMetrics, indexProtocolMetrics } from '../../../commons/functions/financialMetrics';

// Configuración interna
const CHART_HEIGHT = 380;

type CombinedProps = ComponentFunctions.Props;

interface SponsorProfitDatum {
    sponsorId: number | string;
    name: string;
    revenue: number;
    cost: number;
}

const SponsorProfitChartComponent = (props: ComponentFunctions.Props) => {
    const sponsors = props.Sponsors || [];
    // Intentamos acceder a protocolos para agregación consistente (si fueron cargados en otro lugar y están en state global)
    // @ts-ignore - si el mapeo de estado no provee MedicalProtocols, quedará undefined
    const allProtocols: Protocol[] | undefined = (props as any).MedicalProtocols || undefined;
    const protocolMetrics = useMemo(() => allProtocols ? computeProtocolFinancialMetrics(allProtocols) : [], [allProtocols]);
    const protocolMetricsIndex = useMemo(() => indexProtocolMetrics(protocolMetrics), [protocolMetrics]);

    // Estado de selección múltiple
    const [selectedSponsorIds, setSelectedSponsorIds] = useState<(number | string)[]>([]);
    const [orderMode, setOrderMode] = useState<'margin' | 'roi' | 'profit'>('roi');

    const orderOptions: IDropdownOption[] = [
        { key: 'margin', text: 'Ordenar por Rentabilidad (Margen %)' },
        { key: 'roi', text: 'Ordenar por ROI (% sobre costo)' },
        { key: 'profit', text: 'Ordenar por Utilidad (monto)' }
    ];

    // Opciones de dropdown para selección de sponsors (multi-select)
        const sponsorOptions: IDropdownOption[] = useMemo(() => (
            sponsors.map((s: Sponsor) => ({ key: s.id, text: s.person?.businessName || `Sponsor ${s.id}` }))
        ), [sponsors]);

    const filteredSponsors = useMemo(() => {
        if (!selectedSponsorIds.length) return sponsors;
        const set = new Set(selectedSponsorIds);
        return sponsors.filter((s: Sponsor) => set.has(s.id));
    }, [sponsors, selectedSponsorIds]);

    // Generación sintética de ingresos/costos por sponsor
    const data: SponsorProfitDatum[] = useMemo(() => {
        // Si tenemos protocolos y sponsors, particionamos protocolos determinísticamente entre sponsors
        const sponsorsArr = filteredSponsors;
        if (sponsorsArr.length && protocolMetrics.length) {
            // Crear buckets sponsorId -> {revenue, cost}
            const buckets: Record<string | number, { revenue: number; cost: number }> = {};
            sponsorsArr.forEach(s => { buckets[s.id] = { revenue: 0, cost: 0 }; });
            // Partición: hash del protocolId para seleccionar sponsor index
            const sponsorIds = sponsorsArr.map(s => s.id);
            protocolMetrics.forEach(pm => {
                const key = pm.protocolId.toString();
                let h = 0; for (let i = 0; i < key.length; i++) h = (h * 33 + key.charCodeAt(i)) >>> 0;
                const idx = h % sponsorIds.length;
                const sid = sponsorIds[idx];
                buckets[sid].revenue += pm.revenue;
                buckets[sid].cost += pm.cost;
            });
            // Construir datos finales; si algún sponsor quedó sin protocolos, aplicar fallback sintético mínimo
            return sponsorsArr.map(s => {
                let { revenue, cost } = buckets[s.id];
                if (revenue === 0) {
                    const sKey = s.id.toString();
                    let h = 0; for (let i = 0; i < sKey.length; i++) h = (h * 31 + sKey.charCodeAt(i)) >>> 0;
                    const factor = 0.9 + (h % 1000) / 1000 * 0.25;
                    const base = Math.max(1, (s.contractsAssociated?.length || 0)) * 4000;
                    revenue = Math.round(base * factor) + 800; // asegurar diferencia
                    const costRatio = 0.55 + ((factor - 0.9) / 0.25) * 0.15;
                    cost = Math.round(revenue * costRatio);
                }
                return {
                    sponsorId: s.id,
                    name: s.person?.businessName || `Sponsor ${s.id}`,
                    revenue,
                    cost
                } as SponsorProfitDatum;
            });
        }
        // Si no hay protocolos: usar método sintético por sponsor para diversidad
        return filteredSponsors.map((s: Sponsor) => {
            const sKey = s.id.toString();
            let h = 0; for (let i = 0; i < sKey.length; i++) h = (h * 31 + sKey.charCodeAt(i)) >>> 0;
            const factor = 0.9 + (h % 1000) / 1000 * 0.25;
            const base = Math.max(1, (s.contractsAssociated?.length || 0)) * 4500;
            const revenue = Math.round(base * factor) + 500; // +500 para evitar coincidencias exactas
            const costRatio = 0.55 + ((factor - 0.9) / 0.25) * 0.15;
            const cost = Math.round(revenue * costRatio);
            return {
                sponsorId: s.id,
                name: s.person?.businessName || `Sponsor ${s.id}`,
                revenue,
                cost
            } as SponsorProfitDatum;
        });
    }, [filteredSponsors, protocolMetrics]);

    const enriched = useMemo(() => data.map(d => {
        const profit = d.revenue - d.cost;
        const profitability = d.revenue > 0 ? (profit / d.revenue) * 100 : 0; // margen sobre ingresos
        const gainPercent = d.cost > 0 ? (profit / d.cost) * 100 : 0; // ROI sobre costos
        return { ...d, profit, profitability, gainPercent } as any;
    }), [data]);

    const sorted = useMemo(() => {
        const arr = [...enriched];
        switch (orderMode) {
            case 'margin': arr.sort((a, b) => b.profitability - a.profitability); break;
            case 'roi': arr.sort((a, b) => b.gainPercent - a.gainPercent); break;
            case 'profit': arr.sort((a, b) => b.profit - a.profit); break;
        }
        return arr;
    }, [enriched, orderMode]);

    const treemapData = useMemo(() => {
        if (!sorted.length) return [] as any[];
        const n = sorted.length;
        const rankColor = (rank: number) => {
            if (n <= 1) return 'rgb(32,173,155)';
            const t = 1 - rank / (n - 1);
            const c1 = { r: 191, g: 72, b: 43 };
            const c2 = { r: 32, g: 173, b: 155 };
            const r = Math.round(c1.r + (c2.r - c1.r) * t);
            const g = Math.round(c1.g + (c2.g - c1.g) * t);
            const b = Math.round(c1.b + (c2.b - c1.b) * t);
            return `rgb(${r},${g},${b})`;
        };
        return sorted.map((d: any, idx) => {
            let areaValue: number;
            let labelMetric: number;
            if (orderMode === 'margin') {
                areaValue = Math.max(d.profitability, 0.01);
                labelMetric = d.profitability;
            } else if (orderMode === 'roi') {
                areaValue = Math.max(d.gainPercent, 0.01);
                labelMetric = d.gainPercent;
            } else { // profit
                areaValue = Math.max(d.profit, 0.01);
                labelMetric = d.profit;
            }
            return {
                name: d.name,
                value: areaValue,
                revenue: d.revenue,
                cost: d.cost,
                profit: d.profit,
                profitability: Number(d.profitability.toFixed(2)),
                gainPercent: Number(d.gainPercent.toFixed(2)),
                itemStyle: { color: rankColor(idx) },
                label: {
                    formatter: orderMode === 'profit'
                        ? `{b}\n${(labelMetric/1000).toFixed(1)}k`
                        : `{b}\n${Number(labelMetric.toFixed(1))}%`
                }
            };
        });
    }, [sorted, orderMode]);

    const option = useMemo(() => ({
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item',
            formatter: (info: any) => {
                const d = info.data || {};
                return [
                    `<b>${d.name || ''}</b>`,
                    `Ingresos: ${d.revenue?.toLocaleString?.()}`,
                        `Costos: ${d.cost?.toLocaleString?.()}`,
                        `Utilidad: ${d.profit?.toLocaleString?.()}`,
                        `Margen (sobre ingresos): ${d.profitability}%`,
                        `ROI (sobre costo): ${d.gainPercent}%`,
                        (orderMode === 'profit'
                            ? `Área = Utilidad (monto): ${d.profit?.toLocaleString?.()}`
                            : `Área = ${(orderMode === 'margin') ? 'Margen %' : 'ROI %'} (${orderMode === 'margin' ? d.profitability : d.gainPercent}%)`)
                ].join('<br/>' );
            }
        },
        toolbox: { feature: { saveAsImage: {}, restore: {} }, right: 10 },
        series: [
            {
                name: 'Sponsors',
                type: 'treemap',
                roam: true,
                nodeClick: 'zoomToNode',
                breadcrumb: { show: true },
                upperLabel: { show: false },
                label: { show: true },
                data: treemapData,
                animationDuration: 600,
                animationEasing: 'quarticOut'
            }
        ]
    }), [treemapData, orderMode]);

    const onChangeSponsors = (items: IDropdownOption[] | undefined) => {
        const ids = (items || []).map(i => i.key as number | string);
        setSelectedSponsorIds(ids);
    };

        // For multiSelect Dropdown, ensure consistent type (string[] or number[]). Convert to string[] to avoid union issues.
        const selectedKeys = selectedSponsorIds.map(id => id.toString());

    return (
        <div style={{ width: '100%' }}>
            <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 600 }}>Filtro:</div>
                    <div style={{ minWidth: 260, flex: '1 1 320px' }}>
                        <Dropdown
                            placeholder="Seleccione sponsors"
                            multiSelect
                            selectedKeys={selectedKeys}
                            options={sponsorOptions}
                            onChange={(_, opt) => {
                                if (!opt) return;
                                                setSelectedSponsorIds(prev => {
                                                    const keyStr = opt.key!.toString();
                                                    if (opt.selected) return [...prev, keyStr];
                                                    return prev.filter(id => id.toString() !== keyStr);
                                                });
                            }}
                        />
                    </div>
                    {selectedSponsorIds.length > 0 && (
                        <DefaultButton onClick={() => setSelectedSponsorIds([])}>Mostrar todos</DefaultButton>
                    )}
                    <div style={{ fontWeight: 600, marginLeft: 8 }}>Orden:</div>
                    <div style={{ width: 220 }}>
                        <Dropdown
                            selectedKey={orderMode}
                            options={orderOptions}
                            onChange={(_, opt) => opt && setOrderMode(opt.key as any)}
                        />
                    </div>
                    <div style={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>
                        Tamaño = {orderMode === 'margin' ? 'Margen %' : orderMode === 'roi' ? 'ROI %' : 'Utilidad'}
                    </div>
                </div>
            </div>
            <div style={{ width: '100%', height: CHART_HEIGHT }}>
                <ReactECharts notMerge={true} lazyUpdate={true} option={option} style={{ height: '100%', width: '100%' }} />
            </div>
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(SponsorProfitChartComponent as any);


